import { useEffect } from "react";
import {
  SEO_BY_LANG,
  SUPPORTED_SEO_LANGS,
  SeoLang,
} from "@/lib/seo-config";
import { originFor, urlFor } from "@/lib/domain-config";

/**
 * Sets <title>, meta description, OG/Twitter tags, canonical, and hreflang
 * alternates for the landing page based on the active language.
 *
 * Cleanup restores nothing — this is intentional. The next route navigation
 * will overwrite the same tags via the same hook (or leave them as-is if no
 * other page uses this hook). Hreflang/canonical tags are removed on unmount
 * since they are only relevant for the landing page.
 */
export function useLocalizedSeo(language: string) {
  useEffect(() => {
    const lang = (SUPPORTED_SEO_LANGS as string[]).includes(language)
      ? (language as SeoLang)
      : "da";
    const meta = SEO_BY_LANG[lang];
    // Each language has its own canonical origin (ccTLD). The path is "/"
    // for ccTLD languages and a prefix for international (es/fr/pt).
    const pageUrl = urlFor(lang).replace(/\/$/, "") || urlFor(lang);
    // Per-language OG image with localized headline; falls back handled at file-system level.
    const ogImageUrl = `${originFor(lang)}/og/landing-${lang}.jpg`;

    // <html lang="..">
    document.documentElement.lang = lang;

    // <title>
    document.title = meta.title;

    // Helper to upsert a <meta> tag
    const upsertMeta = (selector: string, attrs: Record<string, string>) => {
      let el = document.head.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
        document.head.appendChild(el);
      } else {
        Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
      }
      return el;
    };

    upsertMeta('meta[name="description"]', { name: "description", content: meta.description });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "Relark" });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: meta.title });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: meta.description });
    upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: meta.ogLocale });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: pageUrl });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: ogImageUrl });
    upsertMeta('meta[property="og:image:secure_url"]', { property: "og:image:secure_url", content: ogImageUrl });
    upsertMeta('meta[property="og:image:type"]', { property: "og:image:type", content: "image/jpeg" });
    upsertMeta('meta[property="og:image:width"]', { property: "og:image:width", content: "1200" });
    upsertMeta('meta[property="og:image:height"]', { property: "og:image:height", content: "630" });
    upsertMeta('meta[property="og:image:alt"]', { property: "og:image:alt", content: meta.title });
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: meta.title });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: meta.description });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: ogImageUrl });
    upsertMeta('meta[name="twitter:image:alt"]', { name: "twitter:image:alt", content: meta.title });

    // Canonical (self-referencing per language → points to the lang's ccTLD)
    const canonicalHref = urlFor(lang);
    let canonical = document.head.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]'
    );
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalHref;

    // hreflang alternates — remove any previous, then re-add full set
    document.head
      .querySelectorAll('link[rel="alternate"][hreflang]')
      .forEach((n) => n.remove());

    SUPPORTED_SEO_LANGS.forEach((l) => {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = l;
      // Cross-domain hreflang — each alternate points to its own ccTLD origin.
      link.href = urlFor(l);
      document.head.appendChild(link);
    });
    // x-default → International (relark.app) so users without a matching
    // language get the English version, not the Danish one.
    const xDefault = document.createElement("link");
    xDefault.rel = "alternate";
    xDefault.hreflang = "x-default";
    xDefault.href = urlFor("en");
    document.head.appendChild(xDefault);

    return () => {
      // Strip hreflang + canonical when leaving the landing page
      document.head
        .querySelectorAll('link[rel="alternate"][hreflang]')
        .forEach((n) => n.remove());
      const c = document.head.querySelector('link[rel="canonical"]');
      if (c) c.remove();
    };
  }, [language]);
}
